/**
 * @file useBulkQRGenerator.js
 * @description React hook that orchestrates bulk QR code generation, ZIP
 * compression, and file download while exposing granular progress state.
 *
 * Design decisions:
 *  - Items are processed in batches of BATCH_SIZE (20) so the main thread is
 *    yielded regularly, keeping the browser responsive and the UI updates fluid.
 *  - Each canvas is freed immediately after its PNG blob has been extracted
 *    (canvas.width = canvas.height = 0) so V8 can GC the backing bitmap store
 *    without waiting for a GC cycle.
 *  - Cancellation is driven by a `useRef` flag rather than a state variable
 *    because we need the current value inside async closures without stale
 *    captures — and we don't need a re-render when the flag is toggled.
 *  - Per-item errors are logged and counted rather than aborting the batch,
 *    so a single bad row does not destroy a 5,000-item run.
 *  - ZIP compression uses DEFLATE level 1 (fastest) because PNG data is
 *    already compressed; higher levels provide negligible savings at large cost.
 */

import { useState, useRef, useCallback } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { renderQRCodeToCanvas } from '../../../utils/qrCodeUtils';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Number of QR codes to render before yielding back to the browser. */
const BATCH_SIZE = 20;

/**
 * Default QR canvas options that produce a 600×600 px PNG suitable for
 * printing at 300 PPI (2"×2" sticker).
 */
const DEFAULT_QR_OPTIONS = {
    logicalSize: 300,
    scale: 2,
    fixedCanvasSize: 600,
    errorCorrectionLevel: 'M',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Return a ZIP-safe filename by replacing any character that is not a letter,
 * digit, hyphen, or underscore with an underscore.
 *
 * @param {string} name  Raw filename (without extension)
 * @returns {string}  Sanitised filename
 */
function sanitiseFilename(name) {
    return String(name)
        .trim()
        .replace(/[^a-zA-Z0-9_\-]/g, '_') // replace unsafe chars
        .replace(/_+/g, '_')               // collapse consecutive underscores
        .replace(/^_|_$/g, '');            // strip leading/trailing underscores
}

/**
 * Build the timestamp suffix for the ZIP filename.
 * Format: YYYY-MM-DD_HH-mm-ss
 *
 * @returns {string}
 */
function getTimestamp() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return (
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_` +
        `${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`
    );
}

/**
 * Yield control back to the browser event loop via a zero-delay setTimeout.
 * This allows React state updates and user interactions to be processed between
 * QR render batches.
 *
 * @returns {Promise<void>}
 */
function yieldToBrowser() {
    return new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * Extract a PNG Blob from a canvas element.
 *
 * @param {HTMLCanvasElement} canvas
 * @returns {Promise<Blob|null>}
 */
function canvasToBlob(canvas) {
    return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}

/**
 * Free the memory backing a canvas element.  Setting width and height to 0
 * releases the underlying pixel buffer so the garbage collector can reclaim
 * the memory without waiting for the canvas element itself to be GC'd.
 *
 * @param {HTMLCanvasElement} canvas
 */
function freeCanvas(canvas) {
    try {
        canvas.width = 0;
        canvas.height = 0;
    } catch (_) {
        // Non-critical; ignore
    }
}

// ─── Initial state ────────────────────────────────────────────────────────────

/**
 * @typedef {Object} BulkQRProgress
 * @property {number} done        Number of QR codes successfully generated so far
 * @property {number} total       Total number of QR codes to generate
 * @property {number} percent     Completion percentage [0–100]
 * @property {'idle'|'rendering'|'zipping'|'done'|'error'} stage  Current pipeline stage
 * @property {number} elapsedMs   Wall-clock milliseconds since generation started
 * @property {number|null} etaMs  Estimated milliseconds remaining; null when not yet calculable
 */

/** @returns {BulkQRProgress} */
function makeInitialProgress() {
    return {
        done: 0,
        total: 0,
        percent: 0,
        stage: 'idle',
        elapsedMs: 0,
        etaMs: null,
    };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Custom hook that manages the full lifecycle of bulk QR generation.
 *
 * @returns {{
 *   generate: (
 *     items: Array<{evNumber: string, chassisNumber: string}>,
 *     qrOptions?: object,
 *     zipOptions?: {zipBaseName?: string}
 *   ) => Promise<void>,
 *   cancel: () => void,
 *   reset: () => void,
 *   progress: BulkQRProgress,
 *   isRunning: boolean
 * }}
 */
export default function useBulkQRGenerator() {
    const [progress, setProgress] = useState(makeInitialProgress);
    const [isRunning, setIsRunning] = useState(false);

    /**
     * cancelledRef is true while a cancellation has been requested but the
     * async loop has not yet acknowledged it.  We use a ref (not state) so
     * the latest value is always visible inside the async closure without
     * needing to re-create the closure.
     */
    const cancelledRef = useRef(false);

    // ── Internal helpers ──────────────────────────────────────────────────────

    /**
     * Convenience wrapper that updates progress state and also computes ETA
     * from the elapsed time and completion ratio.
     *
     * @param {Partial<BulkQRProgress>} patch
     * @param {number} startTime  performance.now() at generation start
     */
    const updateProgress = useCallback((patch, startTime) => {
        setProgress((prev) => {
            const next = { ...prev, ...patch };

            // Recompute derived fields whenever done/total change
            const elapsed = performance.now() - startTime;
            next.elapsedMs = Math.round(elapsed);

            if (next.done > 0 && next.total > 0 && next.done < next.total) {
                // Linear ETA: (elapsed / done) * remaining
                const msPerItem = elapsed / next.done;
                next.etaMs = Math.round(msPerItem * (next.total - next.done));
            } else {
                next.etaMs = null;
            }

            next.percent = next.total > 0
                ? Math.min(100, Math.round((next.done / next.total) * 100))
                : 0;

            return next;
        });
    }, []);

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Start generating QR codes for all provided items, then bundle them into
     * a ZIP archive and trigger a browser download.
     *
     * @param {Array<{evNumber: string, chassisNumber: string}>} items
     *   The data rows to generate QR codes for.  Each item must have both
     *   `evNumber` and `chassisNumber` as non-empty strings.
     * @param {object} [qrOptions={}]
     *   Override the default QR canvas options (merged with DEFAULT_QR_OPTIONS).
     * @param {{ zipBaseName?: string }} [zipOptions={}]
     *   Options for the output ZIP file.
     * @returns {Promise<void>}
     */
    const generate = useCallback(async (items, qrOptions = {}, zipOptions = {}) => {
        if (!items || items.length === 0) return;

        cancelledRef.current = false;
        setIsRunning(true);

        const startTime = performance.now();
        const total = items.length;

        // Merge caller-supplied options on top of the sensible print-ready defaults
        const resolvedQROptions = { ...DEFAULT_QR_OPTIONS, ...qrOptions };
        const zipBaseName = zipOptions.zipBaseName || 'EV_QR_STICKERS';

        setProgress({
            done: 0,
            total,
            percent: 0,
            stage: 'rendering',
            elapsedMs: 0,
            etaMs: null,
        });

        const zip = new JSZip();
        let doneCount = 0;
        let errorCount = 0;

        try {
            // ── Phase 1: Render QR codes in batches ───────────────────────────
            for (let batchStart = 0; batchStart < total; batchStart += BATCH_SIZE) {
                // Check cancellation at the start of every batch so we exit
                // promptly without burning through the remaining items
                if (cancelledRef.current) break;

                const batchEnd = Math.min(batchStart + BATCH_SIZE, total);
                const batch = items.slice(batchStart, batchEnd);

                for (const item of batch) {
                    if (cancelledRef.current) break;

                    try {
                        const { evNumber, chassisNumber } = item;

                        // Build the QR text value — both identifiers on one line
                        // so a single scan returns the full vehicle identity
                        const qrText = `EV:${evNumber} CHASSIS:${chassisNumber}`;

                        // Merge per-item label lines into the QR options
                        const itemQROptions = {
                            ...resolvedQROptions,
                            label: {
                                ...(resolvedQROptions.label || {}),
                                lines: [
                                    `EV: ${evNumber}`,
                                    `Chassis: ${chassisNumber}`,
                                ],
                                fontSize: 18,
                                fontFamily: 'Nunito, Arial, sans-serif',
                                lineSpacing: 6,
                                paddingTop: 10,
                                align: 'center',
                            },
                        };

                        const canvas = await renderQRCodeToCanvas(qrText, itemQROptions);
                        const blob = await canvasToBlob(canvas);

                        // Free the canvas backing store immediately — we only
                        // need the blob from here on
                        freeCanvas(canvas);

                        if (blob) {
                            // Sanitise each part separately so separating underscore is preserved
                            const safeChassis = sanitiseFilename(chassisNumber);
                            const safeEV = sanitiseFilename(evNumber);
                            const filename = `${safeChassis}_${safeEV}.png`;
                            zip.file(filename, blob);
                        }

                        doneCount++;
                    } catch (itemErr) {
                        // A single bad row must not abort the entire batch
                        console.error(
                            `[useBulkQRGenerator] Failed to generate QR for item`,
                            item,
                            itemErr,
                        );
                        errorCount++;
                        doneCount++; // Count it as processed so progress advances
                    }

                    // Update progress after every single item for smooth UI feedback
                    updateProgress({ done: doneCount, stage: 'rendering' }, startTime);
                }

                // Yield to the browser between batches so React can flush
                // the progress state updates and the UI stays responsive
                await yieldToBrowser();
            }

            // ── Handle cancellation ───────────────────────────────────────────
            if (cancelledRef.current) {
                setProgress((prev) => ({
                    ...prev,
                    stage: 'error',
                    etaMs: null,
                }));
                setIsRunning(false);
                return;
            }

            // ── Phase 2: Compress and download ZIP ────────────────────────────
            updateProgress({ done: doneCount, stage: 'zipping' }, startTime);

            // DEFLATE level 1: fastest compression.  PNG data is already
            // compressed, so higher levels save almost nothing while being
            // dramatically slower for large batches.
            const zipBlob = await zip.generateAsync(
                {
                    type: 'blob',
                    compression: 'DEFLATE',
                    compressionOptions: { level: 1 },
                },
                // Progress callback from JSZip (fires on every chunk)
                (metadata) => {
                    // Map JSZip's 0-100 zip progress onto a secondary visual
                    // update so the user can see the zipping phase is active
                    updateProgress(
                        {
                            done: doneCount,
                            stage: 'zipping',
                            // Provide a secondary "zip percent" feel via etaMs=null
                        },
                        startTime,
                    );
                },
            );

            const zipFilename = `${zipBaseName}_${getTimestamp()}.zip`;
            saveAs(zipBlob, zipFilename);

            // Final elapsed time snapshot
            const finalElapsed = Math.round(performance.now() - startTime);
            setProgress({
                done: doneCount,
                total,
                percent: 100,
                stage: 'done',
                elapsedMs: finalElapsed,
                etaMs: null,
            });
        } catch (fatalErr) {
            console.error('[useBulkQRGenerator] Fatal error during generation:', fatalErr);
            const elapsed = Math.round(performance.now() - startTime);
            setProgress((prev) => ({
                ...prev,
                stage: 'error',
                elapsedMs: elapsed,
                etaMs: null,
            }));
        } finally {
            setIsRunning(false);
        }
    }, [updateProgress]);

    /**
     * Request cancellation of the current generation run.
     * The loop checks this flag between items, so the effect is near-immediate
     * but not instantaneous.
     */
    const cancel = useCallback(() => {
        cancelledRef.current = true;
    }, []);

    /**
     * Reset all progress state back to idle so the user can start a new
     * generation run without unmounting the component.
     */
    const reset = useCallback(() => {
        cancelledRef.current = false;
        setIsRunning(false);
        setProgress(makeInitialProgress());
    }, []);

    return { generate, cancel, reset, progress, isRunning };
}
